import type { UIMessage } from "@ai-sdk/react";
import { shouldSuppressToolCall } from "./toolCallFriendlyNames";

export interface ChatToolLikePart extends Record<string, unknown> {
  type: string;
  state?: string;
  toolName?: string;
  input?: unknown;
  output?: unknown;
  errorText?: string;
  toolCallId?: string;
}

export interface NormalizedToolPart extends ChatToolLikePart {
  _resolvedToolName?: string;
}

interface MessagePartLike extends Record<string, unknown> {
  type?: string;
  state?: string;
  toolName?: string;
  input?: unknown;
}

export function getPartType(part: unknown): string | undefined {
  if (!part || typeof part !== "object") return undefined;
  const type = (part as { type?: unknown }).type;
  return typeof type === "string" ? type : undefined;
}

export function isThinkingPart(part: unknown): boolean {
  const type = getPartType(part);
  return type === "thinking" || type === "reasoning";
}

export function isToolLikePart(part: unknown): part is ChatToolLikePart {
  const type = getPartType(part);
  return !!type && (type === "dynamic-tool" || type.startsWith("tool-"));
}

export function getToolPartName(part: ChatToolLikePart): string | undefined {
  return part.type === "dynamic-tool"
    ? part.toolName
    : part.type.startsWith("tool-")
      ? part.type.slice(5)
      : undefined;
}

export function normalizeToolPart(part: ChatToolLikePart): NormalizedToolPart {
  return {
    ...part,
    _resolvedToolName: getToolPartName(part),
  };
}

export function shouldSuppressToolPart(part: ChatToolLikePart): boolean {
  const name = getToolPartName(part);
  return !!name && shouldSuppressToolCall(name, part.input);
}

export function getToolFallbackResult(part: ChatToolLikePart): unknown {
  if (part.state === "output-available") return part.output;
  if (part.state === "output-error") return { error: part.errorText };
  return null;
}

export function isRenderableToolOverridePart(part: ChatToolLikePart, compact: boolean): boolean {
  return compact || part.type.startsWith("tool-");
}

export function getLastRenderablePart(messages: UIMessage[]): MessagePartLike | undefined {
  const last = messages[messages.length - 1];
  if (!last?.parts?.length) return undefined;
  for (let i = last.parts.length - 1; i >= 0; i -= 1) {
    const part = last.parts[i] as MessagePartLike;
    if (isToolLikePart(part) && shouldSuppressToolPart(part)) continue;
    return part;
  }
  return undefined;
}

export function isToolPartRunning(part: MessagePartLike | undefined): boolean {
  if (!part || !isToolLikePart(part)) return false;
  return part.state === "input-streaming" || part.state === "input-available";
}
